import GridTile from "../../grid/GridTile";
import PathPlanFinder from "../../path/PathPlanFinder";
import Character from "../Character";
import TurnAction from "../turn/TurnAction";
import CharacterBehavior from "./CharacterBehavior";

export default class MonsterWanderBehavior extends CharacterBehavior {
    /**
     * @param {number} behaviorId
     * @param {PathPlanFinder} pathPlanFinder 
     * @param {TurnAction} moveTurnAction
     */
    constructor(behaviorId, pathPlanFinder, moveTurnAction) {
        super(behaviorId);

        /**
         * @constant
         * @type {PathPlanFinder}
         */
        this.pathPlanFinder = pathPlanFinder;
        /**
         * @constant
         * @type {TurnAction}
         */
        this.moveTurnAction = moveTurnAction;
    }

    /**
     * Applies the behavior, but only if it is appropriate for the current state of the world.
     * @override
     * @param {Character} character
     * @return {boolean} True if the behavior was applied.
     */
    applyIfAppropriate(character) {
        const position = character.position;

        // If we have no path, then there's a % change we just wait for a bit.
        if (position.pathPlan.length === 0) {
            // Wait 3 seconds between each wander move.
            if (Date.now() - character.pathCompletedTime < 3000) {
                return false;
            }

            // We don't want all the monsters to move at the same turn every time. Each monster has a random
            // chance to do nothing during this turn.
            if (Math.random() < 0.75) {
                return false;
            }
        }

        const currentTile = position.tile;
        if (!currentTile) {
            return false;
        }

        const target = character.target;

        // If we have no path, then let's choose a new destination.
        if (position.pathPlan.length === 0) {
            const targetTile = this.chooseRandomNeighborTile(currentTile);
            if (!targetTile) {
                return false;
            }

            // Update the target and find a path to it.
            target.setTargetTile(targetTile, null, this.behaviorId);
            this.pathPlanFinder.findMonsterPathPlan(character.position.tile, targetTile, character.position.pathPlan);
        }

        // Do not act on this target if this behavior does not own it.
        if (target.behaviorId !== this.behaviorId) {
            return;
        }

        // Update the turn action.
        character.turnAction = this.moveTurnAction;

        return true;
    }

    /**
     * @private
     * @param {GridTile} tile
     * @returns {GridTile}
     */
    chooseRandomNeighborTile(tile) {
        let neighbor = null;

        const r = Math.random();

        if (r < 0.15) {
            // Left / Right
            neighbor = tile.getNeighborLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }
        else if (r < 0.3) {
            // Right / Left
            neighbor = tile.getNeighborRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }
        else if (r < 0.45) {
            // TopLeft / TopRight
            neighbor = tile.getNeighborTopLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborTopRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }
        else if (r < 0.6) {
            // TopRight / TopLeft
            neighbor = tile.getNeighborTopRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborTopLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }
        else if (r < 0.75) {
            // BottomLeft / BottomRight
            neighbor = tile.getNeighborBottomLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborBottomRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }
        else if (r < 0.9) {
            // BottomRight / BottomLeft
            neighbor = tile.getNeighborBottomRight();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
            neighbor = tile.getNeighborBottomLeft();
            if (this.isGoodTargetTile(neighbor)) {
                return neighbor;
            }
        }

        return null;
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @returns {boolean} 
     */
    isGoodTargetTile(tile) {
        if (!tile || !tile.isTraversable()) {
            return false;
        }

        // Is there solid footing beneath the tile?
        const bottom = tile.getNeighborBottom();
        if (!bottom || bottom.isTraversable()) {
            return false;
        }

        return true;
    }

}